import React, { createContext, useCallback, useContext, useState, ReactNode, CSSProperties } from 'react';

import AgToast from '../../stories/Components/AgToast';

interface ToastState {
  id?: string;
  open: boolean;
  severity: Color;
  message: string | ReactNode;
  options?: ToastOptions;
}

interface ToastOptions {
  id?: string; // Optional identifier
  time?: number; // Custom auto-hide duration
  temporary?: boolean;
  style?: CSSProperties; // Custom styling for AgToast
}

interface ToastContextType {
  // eslint-disable-next-line no-unused-vars
  toast: (severityOrMessage: string | Color, message?: string | ReactNode, options?: ToastOptions) => void;
}

type Color = 'success' | 'error' | 'neutral';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DEFAULT_SEVERITY: Color = 'success';

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<ToastState>({
    open: false,
    severity: DEFAULT_SEVERITY,
    message: '',
  });

  const toast = useCallback(
    (severityOrMessage: string | Color, message?: string | ReactNode, options?: ToastOptions) => {
      const severity = typeof severityOrMessage === 'string' && ['success', 'error', 'neutral'].includes(severityOrMessage)
        ? (severityOrMessage as Color)
        : DEFAULT_SEVERITY;
      const resolvedMessage = message || (typeof severityOrMessage === 'string' ? severityOrMessage : '');

      setSnackbar({ open: true, severity, message: resolvedMessage, options, id: options?.id });
    },
    []
  );

  const handleClose = useCallback(() => setSnackbar((prev) => ({ ...prev, open: false })), []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <AgToast
        open={snackbar.open}
        severity={snackbar.severity}
        message={snackbar.message}
        onIconClick={handleClose}
        {...snackbar.options}
      />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
